import type {
  Subscription,
  SubscriptionKind,
  SubscriptionsResponse,
  SubscriptionStatusResponse,
  BulkCreateSubscriptionsResponse,
} from '@/types'
import { request, buildQueryString } from './client'

export type CreateSubscriptionData = {
  subscribableType: 'Card' | 'SealedProduct' | 'List' | 'Deck'
  subscribableId?: number
  shareToken?: string
  kind: SubscriptionKind
  thresholdValue?: number
  referencePrice?: number
  oneTime?: boolean
}

export type UpdateSubscriptionData = {
  active?: boolean
  thresholdValue?: number
  referencePrice?: number
  oneTime?: boolean
  notifyOnAnyChange?: boolean
  notifyOnMatch?: boolean
}

export async function getSubscriptions(): Promise<SubscriptionsResponse> {
  return request<SubscriptionsResponse>('/subscriptions')
}

export async function createSubscription(
  data: CreateSubscriptionData,
  idempotencyKey?: string,
): Promise<{ subscription: Subscription }> {
  return request<{ subscription: Subscription }>('/subscriptions', {
    method: 'POST',
    body: data,
    idempotencyKey,
  })
}

export async function updateSubscription(
  id: number,
  data: UpdateSubscriptionData,
  idempotencyKey?: string,
): Promise<{ subscription: Subscription }> {
  return request<{ subscription: Subscription }>(`/subscriptions/${id}`, {
    method: 'PATCH',
    body: data,
    idempotencyKey,
  })
}

export async function deleteSubscription(id: number, idempotencyKey?: string): Promise<void> {
  await request<void>(`/subscriptions/${id}`, { method: 'DELETE', idempotencyKey })
}

export async function getSubscriptionsForItem(
  type: 'Card' | 'SealedProduct',
  id: number,
): Promise<SubscriptionsResponse> {
  const query = buildQueryString({ type, id })
  return request<SubscriptionsResponse>(`/subscriptions/for_item${query}`)
}

export async function getSubscriptionStatus(
  subscribableType: string,
  subscribableId: number,
): Promise<SubscriptionStatusResponse> {
  const query = buildQueryString({ subscribableType, subscribableId })
  return request<SubscriptionStatusResponse>(`/subscriptions/status${query}`)
}

export async function bulkCreateFromBuylist(
  kind: SubscriptionKind,
  thresholdValue: number,
  idempotencyKey?: string,
): Promise<BulkCreateSubscriptionsResponse> {
  return request<BulkCreateSubscriptionsResponse>('/subscriptions/bulk_create_from_buylist', {
    method: 'POST',
    body: { kind, thresholdValue },
    idempotencyKey,
  })
}
